import { db, firebaseConfig, auth } from '@/firebase/config'
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  runTransaction
} from 'firebase/firestore'
import { decryptFields, encryptFields } from './encryptionService'
import { getStripePromise } from '@/utils/stripeLoader'
import {
  getOrCreateAnonId,
  getDeviceType,
  getUtmFirst,
  getUtmLast
} from '@/utils/trackingSpine'

export const savePaymentRecord = async (userId, email, planType, amount) => {
  try {
    const paymentData = {
      userId,
      email,
      planType,
      amount,
      status: 'completed',
      createdAt: serverTimestamp(),
      paymentDate: new Date().toISOString()
    }

    const paymentRef = await addDoc(
      collection(db, 'payments'),
      encryptFields(paymentData)
    )

    const subscriptionData = {
      subscription: {
        planType,
        amount,
        status: 'active',
        startDate: new Date().toISOString(),
        paymentId: paymentRef.id
      },
      email: encryptFields({ email }).email,
      updatedAt: serverTimestamp()
    }

    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, subscriptionData)

    return { success: true, paymentId: paymentRef.id }
  } catch (error) {
    // Error saving payment record - log error type only
    return { success: false, error: 'Failed to save payment record' }
  }
}

export const checkAndSavePaymentRecord = async (
  userId,
  email,
  planType,
  amount,
  sessionId
) => {
  // try {
  //   // Use Firestore transaction to prevent race conditions
  //   const result = await runTransaction(db, async (transaction) => {
  //     // Check if payment record already exists with this sessionId
  //     const paymentsRef = collection(db, 'payments')
  //     const q = query(paymentsRef, where('sessionId', '==', sessionId))
  //     const querySnapshot = await getDocs(q)
  //     if (!querySnapshot.empty) {
  //       // Payment record already exists, no need to create duplicate
  //       return {
  //         success: true,
  //         paymentId: querySnapshot.docs[0].id,
  //         alreadyExists: true
  //       }
  //     }
  //     // Payment record doesn't exist, create new one atomically
  //     const paymentData = {
  //       userId,
  //       email,
  //       planType,
  //       amount,
  //       sessionId,
  //       status: 'completed',
  //       createdAt: serverTimestamp(),
  //       paymentDate: new Date().toISOString()
  //     }
  //     const paymentRef = doc(collection(db, 'payments'))
  //     transaction.set(paymentRef, encryptFields(paymentData))
  //     const subscriptionData = {
  //       subscription: {
  //         planType,
  //         amount,
  //         status: 'active',
  //         startDate: new Date().toISOString(),
  //         paymentId: paymentRef.id
  //       },
  //       email: encryptFields({ email }).email,
  //       updatedAt: serverTimestamp()
  //     }
  //     const userRef = doc(db, 'users', userId)
  //     transaction.update(userRef, subscriptionData)
  //     return { success: true, paymentId: paymentRef.id, alreadyExists: false }
  //   })
  //   return result
  // } catch (error) {
  //   // Error checking and saving payment record - log error type only
  //   return { success: false, error: 'Failed to save payment record' }
  // }
}

export const getPaymentRecord = async (sessionId) => {
  try {
    const paymentRef = doc(db, 'payments', sessionId)
    const paymentDoc = await getDoc(paymentRef)
    return decryptFields(paymentDoc.data())
  } catch (_error) {
    // Error getting payment record - log error type only
    return { success: false, error: 'Failed to retrieve payment record' }
  }
}

export const getLatestPaymentRecord = async (userId) => {
  try {
    const paymentsRef = collection(db, 'payments')
    const q = query(
      paymentsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(1)
    )
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return { success: false, error: 'No payment records found' }
    }

    return decryptFields(querySnapshot.docs[0].data())
  } catch (_error) {
    // Error getting latest payment record - log error type only
    return { success: false, error: 'Failed to retrieve payment record' }
  }
}
export const getUserPaymentRecords = async (userId) => {
  try {
    const paymentsRef = collection(db, 'payments')
    const q = query(paymentsRef, where('userId', '==', userId))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => decryptFields(doc.data()))
  } catch (_error) {
    // Error getting user payment records - log error type only
    return { success: false, error: 'Failed to retrieve payment records' }
  }
}

export const getSchoolPaymentRecords = async (schoolId) => {
  try {
    const paymentsRef = collection(db, 'payments')
    const q = query(
      paymentsRef,
      where('schoolId', '==', schoolId),
      orderBy('createdAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...decryptFields(doc.data())
    }))
  } catch (_error) {
    // Error getting school payment records - log error type only
    return { success: false, error: 'Failed to retrieve payment records' }
  }
}
export const updatePaymentStatus = async (paymentId, status) => {
  try {
    const paymentRef = doc(db, 'payments', paymentId)
    await updateDoc(paymentRef, {
      status,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (_error) {
    // Error updating payment status - log error type only
    return { success: false, error: 'Failed to update payment status' }
  }
}
export const updatePaymentSessionStatus = async (paymentId) => {
  try {
    // Use transaction to prevent race conditions and check idempotency
    const result = await runTransaction(db, async (transaction) => {
      const paymentRef = doc(db, 'payments', paymentId)
      const paymentDoc = await transaction.get(paymentRef)

      if (!paymentDoc.exists()) {
        throw new Error('Payment record not found')
      }

      const paymentData = paymentDoc.data()

      // Idempotency check: if already processed, return success
      if (
        paymentData.sessionStatus === 'completed' &&
        paymentData.paymentStatus === 'Paid'
      ) {
        return { success: true, alreadyProcessed: true }
      }

      // Update payment status atomically
      transaction.update(paymentRef, {
        sessionStatus: 'completed',
        paymentStatus: 'Paid',
        paymentDate: new Date().toISOString(),
        updatedAt: serverTimestamp()
      })

      return { success: true, alreadyProcessed: false }
    })

    return result
  } catch (_error) {
    return { success: false, error: 'Failed to update payment status' }
  }
}

// Process parent payment completion atomically with idempotency
export const processParentPaymentCompletion = async (sessionId) => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/processParentPaymentCompletion`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to process payment completion')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Process school payment completion atomically with idempotency
export const processSchoolPaymentCompletion = async (sessionId, schoolId) => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/processSchoolPaymentCompletion`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          schoolId: schoolId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to process payment completion')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return { success: false, error: error.message }
  }
}
export const saveSchoolPaymentRecord = async (payload) => {
  try {
    const docRef = doc(db, 'payments', payload.sessionId)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      return {
        ...decryptFields(docSnap.data()),
        isExists: true
      }
    } else {
      const paymentData = {
        ...payload,
        createdAt: serverTimestamp(),
        paymentDate: new Date().toISOString()
      }

      await setDoc(docRef, encryptFields(paymentData))
      return {
        ...paymentData,
        isExists: false
      }
    }
  } catch (_error) {
    // Error saving school payment record - log error type only
    return { success: false, error: 'Failed to save payment record' }
  }
}

export const stripeCheckout = async (priceId, studentCount, selectedPlan) => {
  try {
    const stripePromise = getStripePromise()
    const stripe = await stripePromise

    if (!stripe) {
      throw new Error('Stripe failed to load')
    }

    const { error } = await stripe.redirectToCheckout({
      mode: 'subscription',
      lineItems: [
        {
          price: priceId,
          quantity: parseInt(studentCount)
        }
      ],
      successUrl: `${
        window.location.origin
      }/school-plan-options/school-payment?success=true&planType=${selectedPlan}&amount=${parseInt(
        studentCount
      )}&numOfSeats=${studentCount}`,
      cancelUrl: `${window.location.origin}/school-plan-options?plan=${selectedPlan}&numOfSeats=${studentCount}`,
      billingAddressCollection: 'required',
      submitType: 'pay'
    })

    if (error) {
      throw error
    }
  } catch (_error) {
    // Error in stripeCheckout - log error type only
    return { success: false, error: 'Failed to process checkout' }
  }
}

export const getCheckoutSession = async (sessionId) => {
  try {
    const response = await fetch(
      `${firebaseConfig.functionsUrl}/getCheckoutSession?session_id=${sessionId}`
    )
    const data = await response.json()
    return data
  } catch (_error) {
    // Error in getCheckoutSession - log error type only
    return { success: false, error: 'Failed to retrieve checkout session' }
  }
}

export const createParentCheckoutSession = async (planType, userId, email) => {
  try {
    // Get current user's auth token for authentication
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to create checkout session')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/createParentCheckoutSession`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          planType,
          userId,
          email,
          anonId: getOrCreateAnonId() || undefined,
          deviceType: getDeviceType() || undefined,
          utmFirst: getUtmFirst() || undefined,
          utmLast: getUtmLast() || undefined
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      const message = errorData.details
        ? `${errorData.error}: ${errorData.details}`
        : errorData.error || 'Failed to create checkout session'
      throw new Error(message)
    }

    const data = await response.json()
    return { success: true, sessionId: data.sessionId }
  } catch (error) {
    // Remove console.error to prevent sensitive data exposure
    return { success: false, error: error.message }
  }
}

// Verify school payment session server-side using Stripe API
export const verifySchoolPaymentSession = async (sessionId) => {
  try {
    // Get current user's auth token for authentication
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to verify payment session')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/verifySchoolPaymentSession?session_id=${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to verify payment session')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Verify payment session server-side using Stripe API
export const verifyParentPaymentSession = async (sessionId) => {
  try {
    // Get current user's auth token for authentication
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to verify payment session')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/verifyParentPaymentSession?session_id=${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to verify payment session')
    }

    const data = await response.json()
    return data
  } catch (error) {
    // Remove console.error to prevent sensitive data exposure
    return { success: false, error: error.message }
  }
}

export const redirectToParentCheckout = async (planType, userId, email) => {
  try {
    const { success, sessionId, error } = await createParentCheckoutSession(
      planType,
      userId,
      email
    )

    if (!success || !sessionId) {
      throw new Error(error || 'Failed to create checkout session')
    }

    // Load Stripe and redirect to checkout
    const stripePromise = getStripePromise()
    const stripe = await stripePromise
    if (!stripe) {
      throw new Error('Stripe failed to load')
    }

    //console.log('Redirecting to Stripe checkout with sessionId:', sessionId)

    const { error: stripeError } = await stripe.redirectToCheckout({
      sessionId: sessionId
    })

    if (stripeError) {
      //console.error('Stripe redirect error:', stripeError)
      throw new Error(stripeError.message || 'Failed to redirect to checkout')
    }

    return { success: true }
  } catch (error) {
    //console.error('Error in redirectToParentCheckout:', error)
    return {
      success: false,
      error: error.message || 'Failed to redirect to checkout'
    }
  }
}

export const saveParentTrialRecord = async (userId, email, planType) => {
  try {
    const trialData = {
      userId,
      email,
      planType,
      status: 'trialing',
      trialStart: new Date().toISOString(),
      trialEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days from now
      createdAt: serverTimestamp()
    }

    const trialRef = await addDoc(
      collection(db, 'trials'),
      encryptFields(trialData)
    )

    // Update user record with trial information
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, {
      trial: {
        planType,
        status: 'active',
        trialId: trialRef.id,
        trialStart: new Date().toISOString(),
        trialEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
      },
      updatedAt: serverTimestamp()
    })

    return { success: true, trialId: trialRef.id }
  } catch (_error) {
    // Error saving parent trial record - log error type only
    return { success: false, error: 'Failed to save trial record' }
  }
}

export const getParentSubscriptionStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' }
    }

    const userData = userDoc.data()
    const subscription = userData.subscription || null
    const trial = userData.trial || null

    return {
      success: true,
      subscription: subscription ? decryptFields(subscription) : null,
      trial: trial ? decryptFields(trial) : null
    }
  } catch (_error) {
    // Error getting parent subscription status - log error type only
    return { success: false, error: 'Failed to retrieve subscription status' }
  }
}

export const checkTrialExpiration = async (userId) => {
  try {
    const { success, trial, error } = await getParentSubscriptionStatus(userId)

    if (!success) {
      return { success: false, error }
    }

    if (!trial) {
      return { success: false, error: 'No trial found' }
    }

    const trialEndDate = new Date(trial.trialEnd)
    const now = new Date()
    const isExpired = now > trialEndDate

    return {
      success: true,
      isExpired,
      trialEndDate,
      daysRemaining: isExpired
        ? 0
        : Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24))
    }
  } catch (_error) {
    // Error checking trial expiration - log error type only
    return { success: false, error: 'Failed to check trial expiration' }
  }
}

/**
 * Get subscription data from the subscriptions collection by userID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Subscription data with success status
 */
export const getSubscriptionByUserId = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' }
    }

    const subscriptionsRef = collection(db, 'subscriptions')
    const q = query(subscriptionsRef, where('userId', '==', userId))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return {
        success: true,
        subscriptions: [],
        message: 'No subscriptions found for this user'
      }
    }

    const subscriptions = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      subscriptions.push({
        id: doc.id,
        ...data,
        // Convert Firestore timestamps to readable dates
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        trialStart: data.trialStart?.toDate?.() || data.trialStart,
        trialEnd: data.trialEnd?.toDate?.() || data.trialEnd,
        cancellationDate:
          data.cancellationDate?.toDate?.() || data.cancellationDate,
        accessDate: data.accessDate?.toDate?.() || data.accessDate,
        currentPeriodEnd:
          data.currentPeriodEnd?.toDate?.() || data.currentPeriodEnd,
        nextBillingDate:
          data.nextBillingDate?.toDate?.() || data.nextBillingDate,
        currentPeriodStart:
          data.currentPeriodStart?.toDate?.() || data.currentPeriodStart
      })
    })

    // Sort by creation date (newest first)
    subscriptions.sort((a, b) => {
      const dateA =
        a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt)
      const dateB =
        b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt)
      return dateB - dateA
    })

    // Only consider subscriptions as active if:
    // 1. Status is active, trialing, cancel_at_period_end, or nextBillingDate
    // 2. AND they have a stripeSubscriptionId (meaning payment was actually completed)
    // This prevents cancelled checkout sessions from being treated as active subscriptions
    const activeSubscription =
      subscriptions.find(
        (sub) =>
          [
            'active',
            'trialing',
            'cancel_at_period_end',
            'nextBillingDate'
          ].includes(sub.status) && sub.stripeSubscriptionId != null
      ) || null

    return {
      success: true,
      subscriptions,
      activeSubscription,
      totalSubscriptions: subscriptions.length
    }
  } catch (_error) {
    // Error fetching subscriptions by userId - log error type only
    return {
      success: false,
      error: 'Failed to fetch subscription data'
    }
  }
}

export const cancelParentSubscription = async (
  subscriptionId,
  status,
  idTokenOverride = null
) => {
  try {
    let idToken = idTokenOverride
    if (!idToken) {
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('User must be authenticated to cancel subscription')
      }
      idToken = await currentUser.getIdToken(true)
    }

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/cancelParentSubscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          subscriptionId,
          status
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to cancel subscription')
    }

    return { success: true }
  } catch (_error) {
    // Error cancelling parent subscription - log error type only
    return { success: false, error: 'Failed to cancel subscription' }
  }
}

export const resumeParentSubscription = async (subscriptionId) => {
  try {
    // Get current user's auth token for authentication
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to resume subscription')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/resumeParentSubscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          subscriptionId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to resume subscription')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to resume subscription'
    }
  }
}

export const updateParentSubscriptionFeild = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, { hasSubscription: true })
  } catch (_error) {
    // Error updating parent subscription field - log error type only
    return { success: false, error: 'Failed to update subscription field' }
  }
}

export const getParentSubscriptionDetails = async (subscriptionId) => {
  try {
    if (!subscriptionId) {
      return { success: false, error: 'Subscription ID is required' }
    }

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/getParentSubscriptionDetails?subscriptionId=${subscriptionId}`
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error ||
          `HTTP ${response.status}: Failed to fetch subscription details`
      )
    }

    const data = await response.json()
    return { success: true, subscription: data }
  } catch (_error) {
    // Error fetching parent subscription details - log error type only
    return { success: false, error: 'Failed to retrieve subscription details' }
  }
}

export const syncParentSubscription = async (userId, email) => {
  try {
    if (!userId || !email) {
      return { success: false, error: 'User ID and email are required' }
    }

    // Get current user's auth token for authentication
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to sync subscription')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/syncParentSubscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          userId,
          email
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to sync subscription')
    }

    const data = await response.json()
    return { success: true, data }
  } catch (error) {
    // Error syncing parent subscription - log error type only
    return { success: false, error: 'Failed to sync subscription' }
  }
}

export const syncSchoolSubscription = async () => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to sync subscription')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/syncSchoolSubscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error ||
          `HTTP ${response.status}: Failed to sync subscription`
      )
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to sync subscription'
    }
  }
}

export const updateSchoolSubscriptionSeats = async (
  additionalSeats,
  schoolId
) => {
  try {
    if (!additionalSeats || additionalSeats <= 0) {
      return { success: false, error: 'Invalid number of seats' }
    }

    if (!schoolId) {
      return { success: false, error: 'School ID is required' }
    }

    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated to update subscription')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/updateSchoolSubscriptionSeats`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          additionalSeats,
          schoolId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error ||
          `HTTP ${response.status}: Failed to update subscription`
      )
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to update subscription'
    }
  }
}

export const calculateParentUpgradeCost = async (
  subscriptionId,
  newPlanType = 'multipleYearly'
) => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/calculateParentUpgradeCost`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          subscriptionId,
          newPlanType
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to calculate upgrade cost')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to calculate upgrade cost'
    }
  }
}

export const upgradeParentSubscription = async (
  subscriptionId,
  newPlanType = 'multipleYearly'
) => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/upgradeParentSubscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          subscriptionId,
          newPlanType
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to upgrade subscription')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to upgrade subscription'
    }
  }
}

export const updateParentSubscriptionSeats = async (additionalSeats) => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    if (
      !additionalSeats ||
      additionalSeats <= 0 ||
      !Number.isInteger(additionalSeats)
    ) {
      throw new Error('Invalid number of seats. Must be a positive integer.')
    }

    const idToken = await currentUser.getIdToken()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/updateParentSubscriptionSeats`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          additionalSeats
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to update subscription seats')
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to update subscription seats'
    }
  }
}

function isProductionOrigin() {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname || ''
  return host.includes('digipalz.io') || host.includes('digipalz.ca')
}

export const createParentCustomerPortalSession = async () => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User must be authenticated')
    }

    const idToken = await currentUser.getIdToken()
    const useLive = isProductionOrigin()

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/createParentCustomerPortalSession`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
          ...(useLive && { 'X-Use-Live-Stripe': 'true' })
        },
        body: JSON.stringify({ useLive })
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error || 'Failed to create customer portal session'
      )
    }

    const data = await response.json()
    return data
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to create customer portal session'
    }
  }
}
